import { createRootRoute, createRoute, redirect } from '@tanstack/react-router';
import { AppLayout } from '../AppLayout';
import i18n from '../i18n/config';
import { getLegalLocaleFromI18n } from '../legal/legalLocale';
import * as P from './lazyPages';
import { CREATOR } from '../utils/creatorPaths';
import {
  withProtected,
  withSuspense,
  DashboardTanStackGate,
  ArtistManagerAdminOnly,
} from './routeUi';

function legalLocale() {
  return getLegalLocaleFromI18n(i18n.resolvedLanguage || i18n.language);
}

const rootRoute = createRootRoute({
  component: AppLayout,
});

function redirectRoute(path, to, search) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to, search, replace: true });
    },
  });
}

function pageRoute(path, Component, { protect = false, props } = {}) {
  const component = protect ? withProtected(Component, props) : withSuspense(Component, props);
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component,
  });
}

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardTanStackGate,
});

function dashboardChild(path, Component, { protect = false, props, wrapArtist = false } = {}) {
  let component = protect ? withProtected(Component, props) : withSuspense(Component, props);
  if (wrapArtist) {
    const Inner = component;
    component = function ArtistWrapped() {
      return (
        <ArtistManagerAdminOnly>
          <Inner />
        </ArtistManagerAdminOnly>
      );
    };
  }
  return createRoute({
    getParentRoute: () => dashboardRoute,
    path,
    component,
  });
}

function buildDashboardRoutes() {
  const index = createRoute({
    getParentRoute: () => dashboardRoute,
    path: '/',
    component: withSuspense(P.DashboardOverview),
  });

  // FINAL — CREATOR IA
  const music = dashboardChild('music', P.ContentDashboard, { wrapArtist: true });
  const musicReleases = dashboardChild('music/releases', P.ContentDashboard, {
    wrapArtist: true,
    props: { initialView: 'albums' },
  });
  const musicTracks = dashboardChild('music/tracks', P.ContentDashboard, {
    wrapArtist: true,
    props: { initialView: 'tracks' },
  });
  const musicDrafts = dashboardChild('music/drafts', P.ContentDashboard, {
    wrapArtist: true,
    props: { initialView: 'drafts' },
  });
  const musicUpload = dashboardChild('music/upload', P.UnifiedTrackManager, {
    wrapArtist: true,
    props: { mode: 'upload' },
  });
  const musicPublish = dashboardChild('music/publish', P.PublishWizard, { wrapArtist: true });
  const musicAlbumNew = dashboardChild('music/releases/new', P.AlbumForm, { wrapArtist: true });
  const musicAlbumEdit = dashboardChild('music/releases/$albumUuid/edit', P.AlbumForm, {
    wrapArtist: true,
  });
  const musicAlbumDetail = dashboardChild('music/releases/$albumUuid', P.AlbumDetailDashboard, {
    wrapArtist: true,
  });
  const musicTrackDetail = dashboardChild('music/tracks/$trackUuid', P.TrackDetailDashboard, {
    wrapArtist: true,
  });
  const musicTrackEdit = dashboardChild('music/tracks/$trackUuid/edit', P.UnifiedTrackManager, {
    wrapArtist: true,
    props: { mode: 'edit' },
  });

  const sales = dashboardChild('sales', P.AnalyticsOverview, { wrapArtist: true });
  const audience = dashboardChild('audience', P.AudiencePage, { wrapArtist: true });
  const profile = dashboardChild('profile', P.ArtistProfilePage);
  const settings = dashboardChild('settings', P.SettingsPage);

  const playlists = dashboardChild('playlists', P.PlaylistsPage);
  const playlistEdit = dashboardChild('playlists/$id/edit', P.PlaylistEditPage);
  const purchased = dashboardChild('purchased', P.PurchasedPage);

  // Legacy redirects → new Creator IA
  const legacyAnalytics = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'analytics',
    beforeLoad: () => {
      throw redirect({ to: CREATOR.sales, replace: true });
    },
  });
  const legacyContent = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content',
    beforeLoad: () => {
      throw redirect({ to: CREATOR.music, replace: true });
    },
  });
  const legacyContentUpload = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content/upload',
    beforeLoad: ({ location }) => {
      throw redirect({
        to: CREATOR.upload,
        search: location.search,
        replace: true,
      });
    },
  });
  const legacyAlbumsNew = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content/albums/new',
    beforeLoad: ({ location }) => {
      throw redirect({
        to: CREATOR.albumNew,
        search: location.search,
        replace: true,
      });
    },
  });
  const legacyAlbumEdit = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content/albums/$albumUuid/edit',
    beforeLoad: ({ params }) => {
      throw redirect({
        to: CREATOR.albumEdit(params.albumUuid),
        replace: true,
      });
    },
  });
  const legacyAlbumDetail = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content/albums/$albumUuid',
    beforeLoad: ({ params }) => {
      throw redirect({
        to: CREATOR.album(params.albumUuid),
        replace: true,
      });
    },
  });
  const legacyTrackEdit = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content/tracks/$trackUuid/edit',
    beforeLoad: ({ params }) => {
      throw redirect({
        to: CREATOR.trackEdit(params.trackUuid),
        replace: true,
      });
    },
  });
  const legacyAlbums = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content/albums',
    beforeLoad: () => {
      throw redirect({ to: CREATOR.musicReleases, replace: true });
    },
  });
  const legacyTracks = createRoute({
    getParentRoute: () => dashboardRoute,
    path: 'content/tracks',
    beforeLoad: () => {
      throw redirect({ to: CREATOR.musicTracks, replace: true });
    },
  });

  const dashboardFallback = createRoute({
    getParentRoute: () => dashboardRoute,
    path: '$',
    beforeLoad: () => {
      throw redirect({ to: CREATOR.home, replace: true });
    },
  });

  // Static paths before parametric aliases (TanStack match ranking).
  return [
    index,
    musicUpload,
    musicPublish,
    musicAlbumNew,
    musicAlbumEdit,
    musicTrackEdit,
    musicTrackDetail,
    musicAlbumDetail,
    musicDrafts,
    musicReleases,
    musicTracks,
    music,
    sales,
    audience,
    profile,
    settings,
    playlists,
    playlistEdit,
    purchased,
    legacyAnalytics,
    legacyContentUpload,
    legacyAlbumsNew,
    legacyAlbumEdit,
    legacyTrackEdit,
    legacyAlbumDetail,
    legacyAlbums,
    legacyTracks,
    legacyContent,
    dashboardFallback,
  ];
}

export function buildAppRouteTree() {
  const routes = [
    pageRoute('/', P.RootEntryPage),
    pageRoute('/maintenance', P.MaintenancePage),
    pageRoute('/cookie-policy', P.CookiePolicy),
    pageRoute('/auth', P.AuthPage),
    pageRoute('/onboarding', P.CreatorOnboarding),
    pageRoute('/shop', P.ShopPage),
    pageRoute('/discover', P.DiscoverPage),
    pageRoute('/search', P.SearchResultsPage),
    pageRoute('/library', P.LibraryPage),
    redirectRoute('/home', '/'),
    redirectRoute('/shop/albums', '/shop', { type: 'albums' }),
    redirectRoute('/shop/tracks', '/shop', { type: 'tracks' }),
    pageRoute('/album/$id', P.AlbumDetailPage),
    pageRoute('/artist/$username/album/$slug', P.AlbumDetailPage),
    pageRoute('/artist/$uuid/track/$slug', P.TrackDetailPage),
    pageRoute('/artist/$uuid', P.PublicArtistProfile),
    pageRoute('/user/$uuid', P.PublicUserProfile),
    pageRoute('/profile', P.UserProfilePage),
    pageRoute('/plans', P.PlansPage),
    pageRoute('/cancel-subscription/$token', P.CancelSubscriptionPage),
    redirectRoute('/playlists', '/library'),
    redirectRoute('/playlists/create', '/library'),
    pageRoute('/playlists/$id', P.PlaylistDetail, { protect: true }),
    pageRoute('/purchased', P.PurchasedPage, { protect: true }),
    pageRoute('/download/$token', P.DownloadByTokenPage),
    pageRoute('/verify-email/$token', P.VerifyEmailPage),
    pageRoute('/delete-account/confirm/$token', P.ConfirmDeleteAccountPage),
    pageRoute('/change-username/confirm/$token', P.ConfirmUsernameChangePage),
    pageRoute('/change-email/confirm/$token', P.ConfirmEmailChangePage),
    pageRoute('/donations/cancel/confirm/$token', P.ConfirmSubscriptionCancelPage),
    pageRoute('/reset-password/$token', P.ResetPasswordPage),
    pageRoute('/cart', P.ShoppingCart),
    pageRoute('/checkout', P.Checkout),
    pageRoute('/payment/success', P.PaymentSuccess),
    pageRoute('/payment/cancel', P.PaymentCancel),
    pageRoute('/artist/connect/refresh', P.StripeConnectRefreshPage),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/$locale/terms-of-service',
      component: withSuspense(P.TermsOfService),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/$locale/privacy-policy',
      component: withSuspense(P.PrivacyPolicy),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/terms-of-service',
      beforeLoad: () => {
        throw redirect({ to: `/${legalLocale()}/terms-of-service`, replace: true });
      },
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/privacy-policy',
      beforeLoad: () => {
        throw redirect({ to: `/${legalLocale()}/privacy-policy`, replace: true });
      },
    }),
    dashboardRoute,
    ...buildDashboardRoutes(),
    redirectRoute('/user/dashboard', CREATOR.home),
    redirectRoute('/artist/dashboard', CREATOR.home),
    redirectRoute('/manager/dashboard', CREATOR.home),
    redirectRoute('/artist', CREATOR.home),
    redirectRoute('/artist/upload', CREATOR.upload),
    redirectRoute('/artist/upload/wizard', CREATOR.upload),
    redirectRoute('/artist/tracks', CREATOR.musicTracks),
    redirectRoute('/artist/tracks/upload', CREATOR.upload),
    redirectRoute('/artist/albums', CREATOR.musicReleases),
    redirectRoute('/artist/albums/new', CREATOR.albumNew),
    redirectRoute('/artist/albums/create', CREATOR.albumNew),
    redirectRoute('/artist/analytics', CREATOR.sales),
    redirectRoute('/artist/profile', CREATOR.profile),
    redirectRoute('/artist/connect', CREATOR.settings, { tab: 'account' }),
    redirectRoute('/artist/connect/return', CREATOR.settings, {
      tab: 'account',
      stripe_return: '1',
    }),
    redirectRoute('/artist/connect/return/', CREATOR.settings, {
      tab: 'account',
      stripe_return: '1',
    }),
    redirectRoute('/manager', CREATOR.home),
    redirectRoute('/manager/content', CREATOR.music),
    redirectRoute('/manager/analytics', CREATOR.sales),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '$',
      component: withSuspense(P.NotFound),
    }),
  ];

  return rootRoute.addChildren(routes);
}
